import React, { useState, useEffect } from 'react'
import { useCartStore } from '../stores/cartStore'
import { useNavigate } from 'react-router-dom'

const CheckoutPage: React.FC = () => {
  const {
    items,
    shipping,
    isLoadingShipping,
    calculateShipping,
    getSubtotal,
    getShippingCost,
    getGrandTotal
  } = useCartStore()
  const navigate = useNavigate()

  useEffect(() => {
    if (items.length > 0) {
      calculateShipping()
    }
  }, [items.length])

  
  const [customerInfo, setCustomerInfo] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    street: '',
    houseNumber: '',
    city: '',
    zipCode: ''
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    // Create order data
    const orderData = {
      orderNumber: 'ORD-' + Date.now(),
      customerInfo: {
        ...customerInfo,
        name: `${customerInfo.firstName} ${customerInfo.lastName}`.trim(),
        address: `${customerInfo.street} ${customerInfo.houseNumber}`.trim()
      },
      items,
      subtotal: getSubtotal(),
      shippingCost: getShippingCost(),
      total: getGrandTotal(),
      timestamp: new Date().toISOString()
    }

    // Handle order submission
    console.log('Order submitted:', orderData)

    // Navigate to payment page with order data
    navigate('/payment', {
      state: { orderData }
    })
  }


  if (items.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-2xl font-bold mb-4">Warenkorb ist leer</h1>
          <button 
            onClick={() => navigate('/repair')}
            className="bg-indigo-600 text-white px-6 py-3 rounded-lg"
          >
            Jetzt Reparatur auswählen
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4">
        <h1 className="text-3xl font-bold mb-8">Kasse</h1>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Customer Information */}
          <div className="lg:col-span-2">
            <div className="bg-white p-6 rounded-lg shadow">
              <h2 className="text-xl font-semibold mb-6">Ihre Daten</h2>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Vorname *
                    </label>
                    <input
                      type="text"
                      required
                      value={customerInfo.firstName}
                      onChange={(e) => setCustomerInfo({...customerInfo, firstName: e.target.value})}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nachname *
                    </label>
                    <input
                      type="text"
                      required
                      value={customerInfo.lastName}
                      onChange={(e) => setCustomerInfo({...customerInfo, lastName: e.target.value})}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    E-Mail *
                  </label>
                  <input
                    type="email"
                    required
                    value={customerInfo.email}
                    onChange={(e) => setCustomerInfo({...customerInfo, email: e.target.value})}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Telefon *
                  </label>
                  <input
                    type="tel"
                    required
                    value={customerInfo.phone}
                    onChange={(e) => setCustomerInfo({...customerInfo, phone: e.target.value})}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Straße *
                    </label>
                    <input
                      type="text"
                      required
                      value={customerInfo.street}
                      onChange={(e) => setCustomerInfo({...customerInfo, street: e.target.value})}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      placeholder=""
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Hausnr. *
                    </label>
                    <input
                      type="text"
                      required
                      value={customerInfo.houseNumber}
                      onChange={(e) => setCustomerInfo({...customerInfo, houseNumber: e.target.value})}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      placeholder=""
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      PLZ *
                    </label>
                    <input
                      type="text"
                      required
                      value={customerInfo.zipCode}
                      onChange={(e) => setCustomerInfo({...customerInfo, zipCode: e.target.value})}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Stadt *
                    </label>
                    <input
                      type="text"
                      required
                      value={customerInfo.city}
                      onChange={(e) => setCustomerInfo({...customerInfo, city: e.target.value})}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                  </div>
                </div>
                
                <button
                  type="submit"
                  className="w-full bg-indigo-600 text-white py-3 px-4 rounded-lg font-semibold hover:bg-indigo-700 transition-colors"
                >
                  Weiter zur Zahlung
                </button>
              </form>
            </div>
          </div>
          
          {/* Order Summary */}
          <div>
            <div className="bg-white p-6 rounded-lg shadow">
              <h2 className="text-xl font-semibold mb-4">Bestellübersicht</h2>
              
              <div className="space-y-4 mb-6">
                {items.map((item) => (
                  <div key={item.id} className="flex justify-between">
                    <div>
                      <p className="font-medium">{item.title}</p>
                      <p className="text-sm text-gray-600">Anzahl: {item.quantity}</p>
                    </div>
                    <span className="font-medium">{item.price.toFixed(2)}€</span>
                  </div>
                ))}
              </div>
              
              <div className="border-t pt-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Zwischensumme:</span>
                  <span>{getSubtotal().toFixed(2)}€</span>
                </div>
                
                <div className="flex justify-between text-sm">
                  <span>Versand:</span>
                  <span>
                    {isLoadingShipping ? (
                      <span className="text-gray-400">Wird berechnet...</span>
                    ) : getShippingCost() === 0 ? (
                      <span className="text-green-600 font-medium">Kostenlos</span>
                    ) : (
                      `${getShippingCost().toFixed(2)}€`
                    )}
                  </span>
                </div>
                
                {shipping && !shipping.isFreeShipping && (
                  <div className="text-xs text-blue-600 bg-blue-50 p-2 rounded">
                    💡 Noch {(shipping.freeShippingThreshold - shipping.subtotal).toFixed(2)}€ bis zum kostenlosen Versand!
                  </div>
                )}
                
                <hr className="my-2" />
                
                <div className="flex justify-between text-lg font-semibold">
                  <span>Gesamt:</span>
                  <span className="text-indigo-600">
                    {isLoadingShipping ? '...' : getGrandTotal().toFixed(2)}€
                  </span>
                </div>
              </div>

            </div>
          </div>
        </div>

      </div>
    </div>
  )
}

export default CheckoutPage